import supabase from './Supabase';
import { showToast } from './Toasts';
import { getIndexerSignedURL, getFileMD5 } from './Storage';
import { applyFilter } from './ApplyFilter';
import { getUserInfo } from './UserUtil';
import { setRequestAdded } from './Requests';
import { setScriptTags } from './Database';

export async function getIndexInfo(indexId) {
    const { data, error } = await supabase
        .from("indexing")
        .select("*")
        .eq("index_id", indexId)
        .single();

    if (error) {
        console.error("Error fetching index info:", error);
        return null;
    }

    return data || null;
}

export async function getAllIndexingRecords() {
    const { data, error } = await supabase
        .from("indexing")
        .select("*")
        .order("file_name", { ascending: true });

    if (error) {
        console.error("Error fetching indexing records:", error);
        return [];
    }

    return data || [];
}

export async function refreshIndexingRecords() {
    // Fetch current indexing data, filtered by status and sorted by checkout status (checked out files first), then alphabetically
    const { data: updatedIndexing } = await supabase
        .from("indexing")
        .select()
        .in("index_status", ["Not Started", "Checked Out", "In Progress"])
        .order("index_user_id", { ascending: true, nullsFirst: false })
        .order("file_name", { ascending: true });

    // Add email information based on who has checked out each indexing record
    const indexingWithEmails = await Promise.all(
        updatedIndexing?.map(async (indexingRecord) => {
            let email = null;
            if (indexingRecord.index_user_id) {
                const userInfo = await getUserInfo(indexingRecord.index_user_id);
                if (userInfo?.isIndexer === false) {
                    // If user is no longer an indexer, clear the checkout
                    await supabase
                        .from("indexing")
                        .update({ index_user_id: null, index_status: "Not Started" })
                        .eq("index_id", indexingRecord.index_id);
                    indexingRecord.index_user_id = null;
                    indexingRecord.index_status = "Not Started";
                } else {
                    email = userInfo ? userInfo.email : null;
                }
            }
            return {
                ...indexingRecord,
                email: email
            };
        }) || []
    );

    return(indexingWithEmails);
}

export const getFileType = (url: string): 'pdf' | 'other' => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname.toLowerCase();

        // Get the actual file extension (last part after the last dot)
        const lastDotIndex = pathname.lastIndexOf('.');
        if (lastDotIndex === -1) return 'other';

        const extension = pathname.substring(lastDotIndex);

        // Check if it's actually a PDF file (ends with .pdf)
        if (extension === '.pdf') {
            return 'pdf';
        }

        return 'other';
    } catch {
        // Fallback: check if URL contains pdf at the end
        const urlLower = url.toLowerCase();
        if (urlLower.endsWith('.pdf')) {
            return 'pdf';
        }
        return 'other';
    }
};

// This filters based on scripts we already have loaded
export const findTitleMatches = async (title, scripts, user_id) => {
    const userInfo = await getUserInfo(user_id);

    if (!userInfo || (!userInfo.isIndexer && !userInfo.isApprover)) {
        console.warn("User does not have the necessary permissions, skipping title match search.");
        return [];
    }

    if (!title || title.trim().length < 2 || scripts.length === 0) {
        return [];
    }

    const query = title.trim().toLowerCase();

    // Use applyFilter to find matches in the "Script Title" field
    const filters = { "Script Title": query };
    const matches = applyFilter(scripts, filters);

    // Sort matches: exact matches first, then others
    const exactMatches = matches.filter(
        m => m["Script Title"] && m["Script Title"].trim().toLowerCase() === query
    );
    const otherMatches = matches.filter(
        m => !(m["Script Title"] && m["Script Title"].trim().toLowerCase() === query)
    );
    const sortedMatches = [...exactMatches, ...otherMatches].slice(0, 5);
    
    // For each match, get a signed URL
    const matchesWithUrls = await Promise.all(
        sortedMatches.map(async (match) => {
            try {
                const signedUrl = await getIndexerSignedURL(match.Title, user_id);
                return { ...match, signed_url: signedUrl };
            } catch (error) {
                console.error(`Failed to get signed URL for ${match.Title}:`, error);
                return { ...match, signed_url: null };
            }
        })
    );
    return matchesWithUrls;
};

// Whereas this version uses full text search in Supabase
export const findTitleMatchesFullText = async (title, user_id) => {
    const userInfo = await getUserInfo(user_id);

    if (!userInfo || (!userInfo.isIndexer && !userInfo.isApprover)) {
        console.warn("User does not have the necessary permissions, skipping title match search.");
        return [];
    }

    if (!title || title.trim().length < 3 ) {
        return [];
    }

    const query = title.trim().toLowerCase();

    const matches = await supabase
        .from('files')
        .select('*')
        .textSearch('Script Title', query, {
            type: 'websearch'
        });

    if (matches.error || !matches.data || matches.data.length === 0) {
        matches?.error ? console.error('Error searching titles:', matches.error) : console.log('No matches found.');
        return [];
    }

    const exactMatches = matches.data.filter(
        m => m["Script Title"] && m["Script Title"].trim().toLowerCase() === query
    );
    const otherMatches = matches.data.filter(
        m => !(m["Script Title"] && m["Script Title"].trim().toLowerCase() === query)
    );
    const sortedMatches = [...exactMatches, ...otherMatches].slice(0, 5);

    // For each match, get a signed URL
    const matchesWithUrls = await Promise.all(
        sortedMatches.map(async (match) => {
            try {
                const signedUrl = await getIndexerSignedURL(match.Title, user_id);
                return { ...match, signed_url: signedUrl };
            } catch (error) {
                console.error(`Failed to get signed URL for ${match.Title}:`, error);
                return { ...match, signed_url: null };
            }
        })
    );
    return matchesWithUrls;
};

export async function checkOutRow(currentRow, userData) {
    // Checking permissions again here in case userData is stale
    const freshUserInfo = await getUserInfo(userData.user_id);
    if (!freshUserInfo || (!freshUserInfo.isIndexer && !freshUserInfo.isApprover)) {
        return showToast("error", "Unable to check out row.");
    }

    // Get current checkout status
    const { data: row, error } = await supabase
        .from("indexing")
        .select("index_user_id")
        .eq("file_name", currentRow.file_name)
        .single();

    if (error || !row) {
        return showToast("error", "File not found in indexing table");
    }

    // Handle existing checkout
    if (row.index_user_id) {
        const isMyCheckout = row.index_user_id === userData.user_id;
        if (isMyCheckout) {
            // User is continuing their own checkout - get full row data
            const { data: fullRow, error: fullRowError } = await supabase
                .from("indexing")
                .select("*")
                .eq("file_name", currentRow.file_name)
                .single();

            if (fullRowError || !fullRow) {
                return showToast("error", "Error retrieving row data");
            }

            showToast("info", "Checked out, continuing edits.", 2000);
            return fullRow;
        } else {
            showToast("info", "File currently checked out by another user.", 2000);
            return null;
        }
    }

    let md5 = currentRow?.file_hash || '';

    if (!md5) {
        try {
            md5 = await getFileMD5(currentRow.file_name);
        } catch (error) {
            console.error(`Unable to get md5 for ${currentRow.file_name}:`, error);
        }
    };

    // Atomic check-and-set: only update if still available (index_user_id is null)
    const { data: updateResult, error: updateError } = await supabase
        .from("indexing")
        .update({ index_user_id: userData.user_id, file_hash: md5, index_status: "Checked Out" })
        .eq("file_name", currentRow.file_name)
        .is("index_user_id", null)  // Only update if still null
        .select();

    if (updateError) {
        return showToast("error", `Error checking out row: ${updateError.message}`);
    }

    if (!updateResult || updateResult.length === 0) {
        // No rows were updated, meaning someone else checked it out in the meantime
        return showToast("error", "That file was just checked out by another user. Please try a different file.", 2000);
    }

    showToast("info", "Checked out for indexing.", 2000);
    return updateResult[0];
}

// Date validation helper
export const validateDate = (dateValue) => {
    if (!dateValue) return true; // Empty date is valid (optional field)

    const selectedDate = new Date(dateValue);
    const minDate = new Date('1900-01-01');
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today

    return (selectedDate >= minDate) && (selectedDate <= today);
};

// Helper function to check if any form fields have values (indicating partial data entry)
const hasPartialData = (formData: any): boolean => {
    const fieldsToCheck = [
        'script_title',
        'script_writers',
        'draft_date',
        'additional_info',
        'tags'
    ];

    return fieldsToCheck.some(field => {
        const value = formData[field];
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined && value !== '';
    });
};

export async function saveIndex(currentRow: any, formData: any, indexStatus = '', clearCheckout = false) {
    // Check if user has entered any data (indicating work in progress)
    const hasData = hasPartialData(formData);

    var newStatus = (indexStatus !== '') ? indexStatus // Explicit status provided
        : hasData ? "In Progress" // Record has some data entered
            : clearCheckout ? "Not Started" // User cancelled without saving
                : "Checked Out"; // Default status if still checked out without data

    const { data: saveResult, error } = await supabase
        .from("indexing")
        .update({
            ...formData,
            index_status: newStatus,
            index_user_id: clearCheckout ? null : currentRow.index_user_id
        })
        .eq("index_id", currentRow.index_id)
        .eq("index_user_id", currentRow.index_user_id) // Only update if still checked out by same user
        .select();
 
    const message = error
        ? `Error saving row: ${error.message}`
            : (clearCheckout && !indexStatus) ? "Cancelled and checked in." // User cancelled without saving
                : indexStatus ? `Saved with status: ${indexStatus}` // Explicit status provided
                    : hasData
                        ? "Saved in-progress indexing record." // Partial data entered
                        : "Saved record (no data entered)."; // No data entered, but still saved (e.g. checked out without changes)

    showToast(error ? "error" : "info", message, 2000);
}

export async function publishToDatabase(currentRow: any, formData: any, requestID = null) {
    const transformedData = {
        "Script Title": formData.script_title || null,
        "Writer(s)": formData.script_writers || null,
        "Draft Date": formData.draft_date || null,
        "Document Type": formData.document_type || null,
        "Draft": formData.document_version || null,
        "Episode #": formData.episode_number || null,
        "Alternate Titles": formData.alternate_titles || null,
        "Caveat": formData.document_notes || null,
        "Title": currentRow.file_name || null,
        "Type": currentRow.file_type || null,
        "storage_hash": currentRow.file_hash || null,
        "indexing_id": currentRow.index_id || null,
        "Date Added": new Date().toLocaleDateString("en-US")
    }

    const { data: databaseSubmission, error: submissionError } = await supabase
        .from("files")
        .upsert(transformedData)
        .select();

    if (submissionError || !databaseSubmission) {
        const message = `Error submitting to database: ${submissionError?.message || 'Unknown error'}`;
        showToast("error", message);
        return;
    }

    // Write tags to script_tags junction table
    await setScriptTags(databaseSubmission[0].file_id, formData.tags || []);

    if (requestID) {
        // Mark the request as added to database
        await setRequestAdded(requestID, databaseSubmission[0].file_id);
    }

    // Save final data, mark as Completed
    const { data: saveResult, error: saveError } = await supabase
        .from("indexing")
        .update({
            ...formData,
            index_status: "Completed",
            database_id: databaseSubmission[0].file_id  // Link to main files table
        })
        .eq("index_id", currentRow.index_id)
        .select();

    if (saveError || !saveResult) {
        const message = `Error saving indexed record: ${saveError?.message || 'Unknown error'}`;
        showToast("error", message);
        return;
    }

    return showToast("success", "Record submitted to database and marked as Completed!");
}

// Helper function to prepare form data for database operations
export const prepareFormDataForDatabase = (data) => {
    const preparedData = { ...data };

    Object.keys(preparedData).forEach(key => {
        const value = preparedData[key];
        if (typeof value === 'string') {
            const trimmed = value.trim();
            preparedData[key] = trimmed === '' ? null : trimmed;
        }
        if (value === undefined) {
            delete preparedData[key];
        }
    });

    // Handle date formatting for draft_date
    if (preparedData.draft_date) {
        const parsedDate = new Date(preparedData.draft_date + 'T00:00:00');
        preparedData.draft_date = !isNaN(parsedDate.getTime())
            ? parsedDate.toLocaleDateString("en-US")
            : null;
    } else {
        preparedData.draft_date = null;
    }

    return preparedData;
};

export async function getRequestMatches(title: string, writers: string) {
    const { data, error } = await supabase
        .rpc('get_request_matches', {
            search_title: title,
            search_writers: writers,
        });

    if (error) {
        console.error("Error calling get_request_matches:", error);
        return [];
    }

    return data || [];
}

const Indexing = {
    getIndexInfo,
    refreshIndexingRecords,
    getFileType,
    findTitleMatches,
    findTitleMatchesFullText,
    validateDate,
    checkOutRow,
    saveIndex,
    publishToDatabase,
    prepareFormDataForDatabase,
    getRequestMatches
};

export default Indexing;